import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  HELP_SECTIONS,
  HELP_ARTICLES,
  getHelpArticle,
  helpPlainText,
  searchHelp,
} from '@affily/shared/help';
import { LEGAL } from '@affily/shared/constants';
import { Seo } from '../components/Seo.jsx';
import { Input } from '../components/ui.jsx';

/**
 * The help pages: answers to what people ask, one address each so they can be
 * linked from the app, shared, and found in search. The content lives in
 * shared/help.js, so the prerender and the sitemap list exactly these pages.
 */

/**
 * `[text](/path)`, `**bold**` and `` `code` ``, and nothing else, so answers
 * stay plain text in the file. Shared with the blog.
 */
export function Inline({ text }) {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|`[^`]+`)/g).filter(Boolean);
  return parts.map((part, i) => {
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      return link[2].startsWith('/') ? (
        <Link key={i} to={link[2]} className="text-accent hover:underline">
          {link[1]}
        </Link>
      ) : (
        <a key={i} href={link[2]} className="text-accent hover:underline">
          {link[1]}
        </a>
      );
    }
    const bold = part.match(/^\*\*([^*]+)\*\*$/);
    if (bold) return <strong key={i} className="font-medium text-ink">{bold[1]}</strong>;
    const code = part.match(/^`([^`]+)`$/);
    if (code) {
      return (
        <code key={i} className="rounded bg-paper-sunken px-1.5 py-0.5 text-[0.9em]">
          {code[1]}
        </code>
      );
    }
    return part;
  });
}

function Answer({ article }) {
  return (
    <div className="space-y-3 text-ink-muted">
      {article.answer.map((block, i) =>
        typeof block === 'string' ? (
          <p key={i}>
            <Inline text={block} />
          </p>
        ) : (
          <ul key={i} className="list-disc space-y-1.5 pl-5">
            {block.list.map((item, j) => (
              <li key={j}>
                <Inline text={item} />
              </li>
            ))}
          </ul>
        ),
      )}
    </div>
  );
}

const faqJsonLd = (articles) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: articles.map((a) => ({
    '@type': 'Question',
    name: a.question,
    acceptedAnswer: { '@type': 'Answer', text: helpPlainText(a) },
  })),
});

function QuestionLinks({ articles }) {
  return (
    <ul className="divide-y divide-rule rounded-lg border border-rule bg-paper-raised">
      {articles.map((a) => (
        <li key={a.slug}>
          <Link to={`/help/${a.slug}`} className="block px-4 py-3 hover:bg-paper-sunken">
            {a.question}
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function HelpIndex() {
  const [query, setQuery] = useState('');
  const results = searchHelp(query);

  return (
    <>
      <Seo
        title="Help"
        description="How Affily works, answered: links and codes, getting paid, tracking, program dates and currencies, competitions and prizes."
        jsonLd={faqJsonLd(HELP_ARTICLES)}
      />
      <article className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <h1 className="text-4xl font-semibold tracking-tight">Help</h1>
        <p className="mt-4 text-lg text-ink-muted">
          How Affily works, in answers to what people ask. Still stuck?{' '}
          <a href={`mailto:${LEGAL.contactEmail}`} className="text-accent hover:underline">
            Write to us
          </a>
          .
        </p>

        <Input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search — “currency”, “not credited”, “first to 100,000”…"
          aria-label="Search the help"
          className="mt-8"
        />

        {query.trim() ? (
          <section className="mt-8">
            {results.length ? (
              <QuestionLinks articles={results} />
            ) : (
              <p className="text-ink-muted">
                Nothing matches “{query.trim()}”.{' '}
                <a href={`mailto:${LEGAL.contactEmail}`} className="text-accent hover:underline">
                  Ask us
                </a>{' '}
                — the answer will end up here.
              </p>
            )}
          </section>
        ) : (
          HELP_SECTIONS.map((section) => (
            <section key={section.key} className="mt-10">
              <h2 className="mb-3 text-xl font-semibold tracking-tight">{section.title}</h2>
              <QuestionLinks articles={HELP_ARTICLES.filter((a) => a.section === section.key)} />
            </section>
          ))
        )}
      </article>
    </>
  );
}

export function HelpArticle() {
  const { slug } = useParams();
  const article = getHelpArticle(slug);

  if (!article) {
    return (
      <article className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <Seo title="Not found" noindex />
        <h1 className="text-3xl font-semibold tracking-tight">No such question</h1>
        <p className="mt-4 text-ink-muted">
          It may have moved. <Link to="/help" className="text-accent hover:underline">All the answers</Link>
        </p>
      </article>
    );
  }

  const section = HELP_SECTIONS.find((s) => s.key === article.section);
  const related = (article.related ?? []).map(getHelpArticle).filter(Boolean);

  return (
    <>
      <Seo title={article.question} description={helpPlainText(article).slice(0, 160)} jsonLd={faqJsonLd([article])} />
      <article className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <p className="text-sm text-ink-muted">
          <Link to="/help" className="hover:text-ink">
            Help
          </Link>{' '}
          / {section?.title}
        </p>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight">{article.question}</h1>
        <div className="mt-6 text-lg">
          <Answer article={article} />
        </div>

        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-ink-faint">Related</h2>
            <QuestionLinks articles={related} />
          </section>
        )}
        <p className="mt-10 text-sm text-ink-muted">
          Not what you needed?{' '}
          <a href={`mailto:${LEGAL.contactEmail}`} className="text-accent hover:underline">
            Write to us
          </a>{' '}
          · <Link to="/help" className="text-accent hover:underline">All the answers</Link>
        </p>
      </article>
    </>
  );
}

/** A small "how this works" link from the app into one answer. */
export function HelpLink({ slug, children }) {
  return (
    <Link to={`/help/${slug}`} className="text-accent hover:underline" target="_blank" rel="noopener">
      {children ?? 'How this works'}
    </Link>
  );
}
